import { API_BASE_URL } from "./config";

export type RequestOptions = {
  body?: unknown;
  token?: string | null;
  apiKey?: string | null;
  /** Extra headers (e.g. X-Liner-Webhook-Client-Key). Merged after auth/apiKey headers. */
  headers?: Record<string, string>;
};

function buildUrl(path: string): string {
  const base = API_BASE_URL.replace(/\/+$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${base}${p}`;
}

async function request<T = unknown>(
  method: string,
  path: string,
  options: RequestOptions = {}
): Promise<{ status: number; data: T; ok: boolean }> {
  const { body, token, apiKey, headers: extraHeaders } = options;
  const url = buildUrl(path);
  const headers: Record<string, string> = {};
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  if (apiKey) {
    headers["X-API-Key"] = apiKey;
  }
  if (extraHeaders) {
    for (const [k, v] of Object.entries(extraHeaders)) {
      headers[k] = v;
    }
  }
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : "Failed to fetch";
    const base = API_BASE_URL.replace(/\/$/, "");
    return {
      status: 0,
      data: `Network error: ${message}. Is the backend running at ${base}?` as unknown as T,
      ok: false,
    };
  }
  const contentType = res.headers.get("content-type");
  let data: T;
  if (contentType?.includes("application/json")) {
    try {
      data = (await res.json()) as T;
    } catch {
      data = (await res.text()) as unknown as T;
    }
  } else {
    data = (await res.blob()) as unknown as T;
  }
  return { status: res.status, data, ok: res.ok };
}

export const api = {
  get: <T = unknown>(path: string, options?: Omit<RequestOptions, "body">) =>
    request<T>("GET", path, options ?? {}),

  post: <T = unknown>(path: string, options?: RequestOptions) =>
    request<T>("POST", path, options ?? {}),

  put: <T = unknown>(path: string, options?: RequestOptions) =>
    request<T>("PUT", path, options ?? {}),

  patch: <T = unknown>(path: string, options?: RequestOptions) =>
    request<T>("PATCH", path, options ?? {}),
};
